import { useMemo } from 'react';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import useMyPreferences from '../../../../../hooks/api/useMyPreferences';
import { APITypes } from '../../../../../primary-record-types';

export type PreferencesWithProfile = {
    profile: APITypes.medicalProfileService.ProfileListItem;
} & APITypes.userService.UserMedicalProfilePreferences;

export type PreferencesWithProfiles = {
    userProfiles: PreferencesWithProfile[];
    collaboratorProfiles: PreferencesWithProfile[];
};

export type UseProfilesWithPreferencesReturnValue = {
    isLoading: boolean;
    profilesWithPreferences: PreferencesWithProfiles | null;
    update: (preferencesWithProfiles: PreferencesWithProfiles) => void;
    isUpdating: boolean;
};

export type UseProfilesWithPreferencesArgs = {
    onUpdateSuccess: () => void;
};

export default function useProfilesWithPreferences({
    onUpdateSuccess,
}: UseProfilesWithPreferencesArgs): UseProfilesWithPreferencesReturnValue {
    const myPreferences = useMyPreferences({ onUpdatePreferencesSuccess: onUpdateSuccess });
    const { isLoadingProfiles, medicalProfiles, collaboratorMedicalProfiles } = useMedicalProfiles({});

    const profilesWithPreferences = useMemo<PreferencesWithProfiles | null>(() => {
        if (myPreferences.isLoading || isLoadingProfiles) {
            return null;
        }

        const findPreferences = (profile: APITypes.medicalProfileService.ProfileListItem) =>
            myPreferences.preferences?.medicalProfilePreferences.find(
                (p) => p.medicalProfileId === profile.medicalProfileId,
            );

        const returnValue: PreferencesWithProfiles = {
            userProfiles: [],
            collaboratorProfiles: [],
        };

        medicalProfiles.forEach((profile) => {
            const preferences = findPreferences(profile);
            if (preferences) {
                returnValue.userProfiles.push({ profile, ...preferences });
            }
        });

        collaboratorMedicalProfiles.forEach((profile) => {
            const preferences = findPreferences(profile);
            if (preferences) {
                returnValue.collaboratorProfiles.push({ profile, ...preferences });
            }
        });

        return returnValue;
    }, [
        myPreferences.isLoading,
        isLoadingProfiles,
        myPreferences.preferences,
        medicalProfiles,
        collaboratorMedicalProfiles,
    ]);

    const update = (preferencesWithProfiles: PreferencesWithProfiles) => {
        const update: APITypes.userService.UpsertUserPreferencesRequest = {
            medicalProfilePreferences: [],
        };

        const getPreferencesFromProfileWithPreferences = (
            profileWithPreferences: PreferencesWithProfile,
        ): APITypes.userService.UserMedicalProfilePreferences => {
            const { profile: _profile, ...preferences } = profileWithPreferences;
            return preferences;
        };

        preferencesWithProfiles.userProfiles.forEach((profileWithPreferences) => {
            update.medicalProfilePreferences.push(getPreferencesFromProfileWithPreferences(profileWithPreferences));
        });

        preferencesWithProfiles.collaboratorProfiles.forEach((profileWithPreferences) => {
            update.medicalProfilePreferences.push(getPreferencesFromProfileWithPreferences(profileWithPreferences));
        });

        myPreferences.updatePreferences(update);
    };

    return {
        isLoading: myPreferences.isLoading || isLoadingProfiles,
        profilesWithPreferences,
        update,
        isUpdating: myPreferences.isUpdatingPreferences,
    };
}
